const PRINT_AREAS = {
  'T-shirt': {x: 0.34, y: 0.23, width: 0.32, height: 0.42},
  Hoodie: {x: 0.34, y: 0.28, width: 0.32, height: 0.35},
  Sweatshirt: {x: 0.34, y: 0.27, width: 0.32, height: 0.36},
  'Long Sleeve': {x: 0.35, y: 0.24, width: 0.3, height: 0.4},
  'Tank-top': {x: 0.36, y: 0.25, width: 0.28, height: 0.38},
  Mug: {x: 0.3, y: 0.3, width: 0.34, height: 0.34},
  'Tote bag': {x: 0.31, y: 0.3, width: 0.38, height: 0.42},
  default: {x: 0.34, y: 0.24, width: 0.32, height: 0.4},
};
const baseCache = new Map();

export async function loadDesign(file) {
  if (!file || file.type !== 'image/png') throw new Error('Choose a PNG design file.');
  return loadImage(URL.createObjectURL(file), false);
}

export async function renderMockup(canvas, {template, color, design, placement}) {
  const {source, exactColor} = resolveMockupSource(template, color);
  const base = await productBase(source, color, exactColor);
  const context = canvas.getContext('2d');
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(base, 0, 0, canvas.width, canvas.height);
  if (!design) return;

  const area = PRINT_AREAS[template.group] || PRINT_AREAS.default;
  const printRect = fitRect(
    {x: canvas.width * area.x, y: canvas.height * area.y,
      width: canvas.width * area.width, height: canvas.height * area.height},
    5 / 6,
  );
  drawPlacedArtwork(context, design, placement, printRect);
}

export function resolveMockupSource(template, color) {
  const exactSource = template.baseImageUrls?.[color];
  return {
    source: exactSource || template.baseImageUrl,
    exactColor: Boolean(exactSource),
  };
}

export function canvasBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Could not create mockup image.')),
      'image/jpeg', 0.9,
    );
  });
}

export function positionedPrintBlob({design, placement}, width = 4500, height = 5400) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  drawPlacedArtwork(context, design, placement, {x: 0, y: 0, width, height});
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error('Could not create positioned print file.')),
      'image/png',
    );
  });
}


function drawPlacedArtwork(context, design, placement, rect) {
  const geometry = placementGeometry(design, placement, rect);
  context.save();
  context.beginPath();
  context.rect(rect.x, rect.y, rect.width, rect.height);
  context.clip();
  context.drawImage(design, geometry.x, geometry.y, geometry.width, geometry.height);
  context.restore();
}

export function placementGeometry(design, placement, rect) {
  const fit = Math.min(rect.width / design.width, rect.height / design.height);
  const width = design.width * fit * placement.scale;
  const height = design.height * fit * placement.scale;
  const centerX = rect.x + rect.width * (0.5 + placement.x);
  const centerY = rect.y + rect.height * (0.5 + placement.y);
  return {x: centerX - width / 2, y: centerY - height / 2, width, height};
}

function fitRect(rect, aspectRatio) {
  const currentRatio = rect.width / rect.height;
  if (currentRatio > aspectRatio) {
    const width = rect.height * aspectRatio;
    return {...rect, x: rect.x + (rect.width - width) / 2, width};
  }
  const height = rect.width / aspectRatio;
  return {...rect, y: rect.y + (rect.height - height) / 2, height};
}

async function productBase(source, color, exactColor) {
  const key = `${source}:${color}:${exactColor}`;
  if (baseCache.has(key)) return baseCache.get(key);
  const image = await loadImage(source, true);
  if (exactColor || color !== 'BLACK') {
    baseCache.set(key, image);
    return image;
  }
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d', {willReadFrequently: true});
  context.drawImage(image, 0, 0);
  darkenGarment(context, canvas.width, canvas.height);
  baseCache.set(key, canvas);
  return canvas;
}

function darkenGarment(context, width, height) {
  const imageData = context.getImageData(0, 0, width, height);
  const {data} = imageData;
  const background = connectedBackground(data, width, height);
  for (let pixel = 0; pixel < width * height; pixel += 1) {
    if (background[pixel]) continue;
    const index = pixel * 4;
    const luminance = (data[index] + data[index + 1] + data[index + 2]) / 765;
    const shade = Math.round(12 + luminance * 46);
    data[index] = shade;
    data[index + 1] = shade;
    data[index + 2] = shade;
  }
  context.putImageData(imageData, 0, 0);
}

function connectedBackground(data, width, height) {
  const seen = new Uint8Array(width * height);
  const queue = new Uint32Array(width * height);
  let head = 0;
  let tail = 0;
  const enqueue = (pixel) => {
    if (seen[pixel] || !isBackground(data, pixel)) return;
    seen[pixel] = 1;
    queue[tail++] = pixel;
  };
  for (let x = 0; x < width; x += 1) { enqueue(x); enqueue((height - 1) * width + x); }
  for (let y = 0; y < height; y += 1) { enqueue(y * width); enqueue(y * width + width - 1); }
  while (head < tail) {
    const pixel = queue[head++];
    const x = pixel % width;
    const y = Math.floor(pixel / width);
    if (x > 0) enqueue(pixel - 1);
    if (x + 1 < width) enqueue(pixel + 1);
    if (y > 0) enqueue(pixel - width);
    if (y + 1 < height) enqueue(pixel + width);
  }
  return seen;
}

function isBackground(data, pixel) {
  const index = pixel * 4;
  const values = [data[index], data[index + 1], data[index + 2]];
  return Math.min(...values) > 252 && Math.max(...values) - Math.min(...values) < 5;
}

function loadImage(source, anonymous) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (anonymous) image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load mockup base image.'));
    image.src = source;
  });
}
